import CloseIcon from '@ordr-as/icons/CloseIcon';
import type { KdsTaskDetail } from '@ordr-as/ordr-service-types/epos';
import { selectExpoDisplayItems } from '../selectors';
import type { ExpoOrder, FlowGraph } from '../types';
import { formatOrderedAt } from '../utils';
import StationChip from '../board/StationChip';

type Props = {
  groupId: string;
  tasks: KdsTaskDetail[];
  graph: FlowGraph;
  stationNameById: Map<number, string>;
  onClose: () => void;
};

function toOrder(groupId: string, tasks: KdsTaskDetail[]): ExpoOrder {
  const groupTasks = tasks.filter((task) => task.groupId === groupId && task.status !== 'VOIDED');
  const doneCount = groupTasks.filter((task) => task.status === 'DONE').length;
  return {
    groupId,
    bookingOrderId: groupTasks.find((task) => task.bookingOrderId !== undefined)?.bookingOrderId,
    createdUtc: groupTasks
      .map((task) => task.createdUtc)
      .filter((stamp): stamp is string => Boolean(stamp))
      .sort()[0],
    updatedUtc: undefined,
    eatIn: groupTasks.some((task) => task.eatIn === true),
    orderComment: groupTasks.find((task) => task.orderComment)?.orderComment,
    tasks: groupTasks,
    doneCount,
    totalCount: groupTasks.length,
    allDone: groupTasks.length > 0 && doneCount === groupTasks.length,
  };
}

const OrderDetailSheet = ({ groupId, tasks, graph, stationNameById, onClose }: Props) => {
  const order = toOrder(groupId, tasks);
  const items = selectExpoDisplayItems(order, graph);
  const orderedAt = formatOrderedAt(order.createdUtc);

  return (
    <>
      <div className="flex items-start justify-between gap-2 pl-4 pr-2 pt-2 flex-none">
        <div className="text-xl font-bold text-brand-secondary">#{order.bookingOrderId}</div>
        <button
          className="flex-none w-6 h-6 rounded-full flex items-center justify-center text-brand-secondary hover:bg-grey-lightest"
          onClick={onClose}
          title="Close"
        >
          <CloseIcon width={18} height={18} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-5 flex flex-col gap-4">
        {orderedAt && <div className="text-extra-small text-grey-dark">Ordered: {orderedAt}</div>}

        <div className="flex flex-col gap-2">
          <h3 className="m-0 text-extra-small font-semibold uppercase tracking-wide text-grey-dark">Order</h3>
          <div className="rounded bg-brand-white px-4 py-3 flex flex-col gap-2">
            {items.map((item, index) => (
              <div
                key={item.key}
                className={`py-1 flex flex-col items-start gap-1 ${
                  index > 0 ? 'border-t border-grey-lightest pt-2' : ''
                }`}
              >
                <span className="text-small font-semibold text-brand-black">
                  {item.quantity} × {item.name}
                </span>
                {item.modifiers.map((modifier, modifierIndex) => (
                  <span key={modifierIndex} className="text-extra-small text-grey-dark">
                    → {modifier}
                  </span>
                ))}
                {item.comment && <span className="text-extra-small italic text-grey-dark">“{item.comment}”</span>}
                <div className="flex gap-1 flex-wrap">
                  {item.chips.map((chip, chipIndex) => (
                    <StationChip
                      key={chipIndex}
                      label={
                        chip.stationId !== undefined
                          ? stationNameById.get(chip.stationId) ?? 'Unknown station'
                          : 'Unknown station'
                      }
                      state={chip.state}
                    />
                  ))}
                </div>
              </div>
            ))}
            {items.length === 0 && <div className="text-small text-grey-dark py-1">No order lines.</div>}
          </div>
        </div>

        {order.orderComment && (
          <div className="flex flex-col gap-2">
            <h3 className="m-0 text-extra-small font-semibold uppercase tracking-wide text-grey-dark">Comment</h3>
            <div className="rounded bg-brand-white px-4 py-3 text-small italic text-grey-dark">
              “{order.orderComment}”
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default OrderDetailSheet;
